// React Hooks for Diagnoses
// Part of Diagnosis Nomenclature Implementation

import { useState, useEffect } from 'react';
import diagnosisService from '../services/diagnosisService';

/**
 * Hook to fetch all diagnoses
 */
export function useDiagnoses(organizationId, filters = {}) {
  const [diagnoses, setDiagnoses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDiagnoses = async () => {
    if (!organizationId) {
      setDiagnoses([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await diagnosisService.getDiagnoses(organizationId, filters);
      setDiagnoses(data);
    } catch (err) {
      console.error('Error loading diagnoses:', err);
      setError(err.message);
      setDiagnoses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDiagnoses();
  }, [organizationId, JSON.stringify(filters)]);

  const refresh = () => {
    loadDiagnoses();
  };

  return { diagnoses, loading, error, refresh };
}

/**
 * Hook to fetch single diagnosis
 */
export function useDiagnosis(diagnosisId) {
  const [diagnosis, setDiagnosis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDiagnosis = async () => {
    if (!diagnosisId) {
      setDiagnosis(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await diagnosisService.getDiagnosis(diagnosisId);
      setDiagnosis(data);
    } catch (err) {
      console.error('Error loading diagnosis:', err);
      setError(err.message);
      setDiagnosis(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDiagnosis();
  }, [diagnosisId]);

  const refresh = () => {
    loadDiagnosis();
  };

  return { diagnosis, loading, error, refresh };
}

/**
 * Hook for diagnosis mutations
 */
export function useDiagnosisMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createDiagnosis = async (data, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      const id = await diagnosisService.createDiagnosis(data, userId, userName);
      return id;
    } catch (err) {
      console.error('Error creating diagnosis:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateDiagnosis = async (diagnosisId, updates, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      await diagnosisService.updateDiagnosis(diagnosisId, updates, userId, userName);
    } catch (err) {
      console.error('Error updating diagnosis:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteDiagnosis = async (diagnosisId) => {
    try {
      setLoading(true);
      setError(null);
      await diagnosisService.deleteDiagnosis(diagnosisId);
    } catch (err) {
      console.error('Error deleting diagnosis:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    createDiagnosis,
    updateDiagnosis,
    deleteDiagnosis,
    loading,
    error
  };
}

/**
 * Hook to search diagnoses (local + WHO API)
 */
export function useDiagnosisSearch(organizationId) {
  const [searchResults, setSearchResults] = useState({ local: [], whoApi: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchDiagnoses = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
      setSearchResults({ local: [], whoApi: [], total: 0 });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const results = await diagnosisService.searchDiagnoses(searchTerm, organizationId);
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching diagnoses:', err);
      setError(err.message);
      setSearchResults({ local: [], whoApi: [], total: 0 });
    } finally {
      setLoading(false);
    }
  };

  return {
    searchResults,
    searchDiagnoses,
    loading,
    error
  };
}

/**
 * Hook to get diagnoses by biological entity
 */
export function useDiagnosesByBiologicalEntity(organizationId, biologicalEntityId) {
  const [diagnoses, setDiagnoses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDiagnoses = async () => {
    if (!organizationId || !biologicalEntityId) {
      setDiagnoses([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await diagnosisService.getDiagnosesByBiologicalEntity(organizationId, biologicalEntityId);
      setDiagnoses(data);
    } catch (err) {
      console.error('Error loading diagnoses by biological entity:', err);
      setError(err.message);
      setDiagnoses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDiagnoses();
  }, [organizationId, biologicalEntityId]);

  const refresh = () => {
    loadDiagnoses();
  };

  return { diagnoses, loading, error, refresh };
}

/**
 * Hook to get HAI diagnoses
 */
export function useHAIDiagnoses(organizationId, haiType = null) {
  const [diagnoses, setDiagnoses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDiagnoses = async () => {
    if (!organizationId) {
      setDiagnoses([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await diagnosisService.getHAIDiagnoses(organizationId, haiType);
      setDiagnoses(data);
    } catch (err) {
      console.error('Error loading HAI diagnoses:', err);
      setError(err.message);
      setDiagnoses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDiagnoses();
  }, [organizationId, haiType]);

  const refresh = () => {
    loadDiagnoses();
  };

  return { diagnoses, loading, error, refresh };
}

